import React, {useEffect, useMemo, useRef} from 'react';
import lottie, {AnimationItem} from 'lottie-web';
import leftSegmentData from '../../../../assests/json/AnimatedProgress/Segment-Left.json'; // Left-side animation JSON
import rightSegmentData from '../../../../assests/json/AnimatedProgress/Segment-Right.json'; // Right-side animation JSON
import AurProgressChart from '../../aur-progress-chart/AurProgressChart';
import {STEP_PROCESSING_STATUS} from '../AurPizzaTrackerProgress';

function calculateLeftRightStyle(userTotalWidth: number) {
  /**
   * dont't change this initital base value for component.
   * This intial value set as per animation json file's actual size to maintain aspect ratio.
   * If need to increase or decrease it then use width prop of AnimatedProgreesBar component.
   */
  const baseTotalWidth = 152;
  const baseLeftWidth = 58;
  const baseRightWidth = 94;
  // Using fix height as animation json not working as expected when increase height as per width grow
  const baseTotalHeight = 54;

  // Calculate the scaling factor based on the user's total width
  const scalingFactor = userTotalWidth / baseTotalWidth;

  // Dynamically calculate the new left and right widths
  const newLeftWidth = baseLeftWidth * scalingFactor;
  const newRightWidth = baseRightWidth * scalingFactor;

  return {
    leftContainer: {
      width: `${newLeftWidth}px`,
      height: `${baseTotalHeight}px`,
    },
    rightContainer: {
      width: `${newRightWidth}px`,
      height: `${baseTotalHeight}px`,
    },
  };
}

interface AnimatedProgressBarProp {
  width?: number; // Width of progress bar for animated or non animated
  status?: string;
}
interface NonAnimatedBarProp extends AnimatedProgressBarProp {
  stepProcessingStatus?: STEP_PROCESSING_STATUS;
}

export const NonAnimatedProgressBar: React.FC<NonAnimatedBarProp> = ({
  width = 152,
  status,
  stepProcessingStatus,
}) => {
  const isCompleted = useMemo(() => {
    return stepProcessingStatus === 'Completed';
  }, [stepProcessingStatus]);

  return (
    <div
      style={{width: `${width}px`}}
      className="non-animated-progress-container"
      data-testid={
        isCompleted
          ? `non-animated-progress-bar-filled-${status}`
          : `non-animated-progress-bar-unfilled-${status}`
      }
      aria-hidden
    >
      <AurProgressChart
        dataSet={[
          {
            total: 1,
            progress: isCompleted ? 1 : 0,
            filledColor: '#5265FF',
            unfilledColor: '#263449',
            key: isCompleted ? 'filled' : 'unfilled',
          },
        ]}
        layout={{
          barWidth: 4,
          barHeight: 24,
          spaceBetweenBar: 2,
        }}
        fixedWidth={width}
      />
    </div>
  );
};

const AnimatedProgreesBar: React.FC<AnimatedProgressBarProp> = ({
  width = 152,
  status,
}) => {
  const leftContainerRef = useRef<HTMLDivElement | null>(null);
  const rightContainerRef = useRef<HTMLDivElement | null>(null);
  const leftAnimationInstanceRef = useRef<AnimationItem | null>(null);
  const rightAnimationInstanceRef = useRef<AnimationItem | null>(null);

  useEffect(() => {
    if (!leftContainerRef.current || !rightContainerRef.current) {
      console.error('Animation container(s) not found.');
      return;
    }

    // Initialize the left-side animation
    leftAnimationInstanceRef.current = lottie.loadAnimation({
      container: leftContainerRef.current,
      renderer: 'svg',
      loop: false, // Left animation plays only once
      autoplay: true, // Start playing immediately
      animationData: leftSegmentData,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice',
      },
    });

    // Initialize the right-side animation
    rightAnimationInstanceRef.current = lottie.loadAnimation({
      container: rightContainerRef.current,
      renderer: 'svg',
      loop: true, // Right animation loops indefinitely
      autoplay: true, // Start playing immediately
      animationData: rightSegmentData,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice',
      },
    });

    // Cleanup on unmount
    return () => {
      leftAnimationInstanceRef.current?.destroy();
      rightAnimationInstanceRef.current?.destroy();
    };
  }, []);

  const dynamicStyle = calculateLeftRightStyle(width);

  return (
    <div
      className="animated-progress-container"
      data-testid={`animated-progress-bar-${status}`}
      aria-hidden
    >
      <div
        data-testid="left-container-animation"
        ref={leftContainerRef}
        style={dynamicStyle.leftContainer}
      />
      <div
        data-testid="right-container-animation"
        ref={rightContainerRef}
        style={dynamicStyle.rightContainer}
      />
    </div>
  );
};

export default AnimatedProgreesBar;
